import { Controller } from "@hotwired/stimulus"

// Handles the refund_payment_details question
// Shows/hides bank details form based on yes/no answer
// Confirms deletion if user selects "No" with existing data
export default class extends Controller {
  static targets = ["formFields", "formContainer", "yesRadio", "noRadio"]
  static values = {
    hasExistingData: Boolean,
    updateUrl: String
  }

  connect() {
    this.updateFormVisibility()
  }

  handleAnswerChange(event) {
    const selectedValue = event.target.value

    if (selectedValue === 'yes') {
      // Just show the form, don't save anything yet
      // The answer will be saved when they fill out the bank details
      this.updateFormVisibility()
      return
    }

    if (selectedValue === 'no' && this.hasExistingDataValue) {
      // User is switching to "No" but there's existing payment data
      const confirmed = window.confirm(
        'Selecting "No" will delete your saved payment information. Are you sure you want to continue?'
      )

      if (!confirmed) {
        // User cancelled - revert to "Yes"
        this.yesRadioTarget.checked = true
        this.updateFormVisibility()
        return
      }
    }

    // Update form visibility
    this.updateFormVisibility()

    // Save the "no" answer
    this.saveAnswer(selectedValue)
  }

  updateFormVisibility() {
    const isYesSelected = this.yesRadioTarget.checked

    if (isYesSelected) {
      this.formFieldsTarget.style.display = ''
    } else {
      this.formFieldsTarget.style.display = 'none'
    }
  }

  async saveAnswer(answer) {
    try {
      const formData = new FormData()
      formData.append('refund_payment_details[answer]', answer)

      // If "no" is selected, also include destroy flag
      if (answer === 'no') {
        formData.append('refund_payment_details[destroy]', '1')
      }

      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        // Revert radio selection
        if (answer === 'no') {
          this.yesRadioTarget.checked = true
        } else {
          this.noRadioTarget.checked = true
        }
        this.updateFormVisibility()
        return
      }

      // Let Turbo handle the stream response (works for both 2xx and 422 responses)
      const text = await response.text()
      Turbo.renderStreamMessage(text)

      if (response.ok) {
        // If "no" was selected, mark that we no longer have existing data
        if (answer === 'no') {
          this.hasExistingDataValue = false
        }
      }
      // For 422 responses, the turbo stream will show flash error messages
    } catch (error) {
      console.error('Failed to save answer:', error)
      alert('Failed to save your answer. Please try again.')
      // Revert radio selection
      if (answer === 'no') {
        this.yesRadioTarget.checked = true
      } else {
        this.noRadioTarget.checked = true
      }
      this.updateFormVisibility()
    }
  }
}
